import React, { ReactElement, ReactNode } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { FsComponentSize } from 'globals/types';
import defaultLocales from 'globals/locales/default';
import { getNumberId, isLastElement } from 'globals/utils';
import FsXmarkInCircleIcon from 'ui-elements/icons/FsXmarkInCircle';
import CheckMarkInCircleIcon from 'ui-elements/icons/FsCheckMarkInCircle';
import {
  CustomTableCell,
  HeadValue,
  Name,
  Value,
} from './FsSimpleTable.style-componentes';

const displayValue = (
  value: string | number | boolean | ReactElement
): string | number | ReactNode => {
  if (typeof value === 'boolean' && value) {
    return <FsXmarkInCircleIcon data-test="x-mark" />;
  }
  if (typeof value === 'boolean' && !value) {
    return <CheckMarkInCircleIcon data-test="check-mark" />;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return <Value>{value}</Value>;
  }
  return value;
};

const displayCaption = (
  name: string | ReactElement
): string | number | ReactNode => {
  if (typeof name === 'string') {
    return <Name>{name}</Name>;
  }
  return name;
};

export interface FsSimpleTableBodyDataRow {
  id: string | number;
  name: string | ReactElement;
  values: (string | number | boolean | ReactElement)[];
}

export interface FsSimpleTableHeadDataRowItem {
  id: string | number;
  caption: string | ReactElement;
}

export interface FsSimpleTableDataProps {
  rows?: FsSimpleTableBodyDataRow[];
  head?: FsSimpleTableHeadDataRowItem[];
  firstRowCaption?: string;
  withBorder?: boolean;
}

export interface FsSimpleTableProps extends FsSimpleTableDataProps {
  rowSize?:
    | FsComponentSize.XS
    | FsComponentSize.S
    | FsComponentSize.M
    | FsComponentSize.L;
}

const FsSimpleTable: React.FC<FsSimpleTableProps> = ({
  head,
  rows = [],
  firstRowCaption,
  rowSize,
  withBorder = true,
}) => {
  return (
    <TableContainer>
      <Table aria-label={defaultLocales.simpleTable}>
        {head && head?.length && (
          <TableHead>
            <TableRow data-test="table-head-row">
              <CustomTableCell withBorder={withBorder}>
                <HeadValue>{firstRowCaption}</HeadValue>
              </CustomTableCell>
              {head.map(({ id, caption }) => (
                <CustomTableCell
                  key={`head-${id}`}
                  align="right"
                  withBorder={withBorder}
                >
                  {caption && <HeadValue>{caption}</HeadValue>}
                </CustomTableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {rows.map(({ name, values, id }, idx) => (
            <TableRow key={id} data-test="table-row">
              <CustomTableCell
                component="th"
                scope="row"
                withBorder={withBorder}
                rowSize={rowSize || FsComponentSize.M}
                isLast={isLastElement(idx, rows.length)}
              >
                {displayCaption(name)}
              </CustomTableCell>
              {values.map((value) => (
                <CustomTableCell
                  key={getNumberId()}
                  align="right"
                  withBorder={withBorder}
                  rowSize={rowSize}
                  isLast={isLastElement(idx, rows.length)}
                >
                  {displayValue(value)}
                </CustomTableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FsSimpleTable;
